import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setDropdownOpen(false);
  }, []);

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20collage%20with%20sneakers%20handbags%20sunglasses%20and%20fragrance%20bottles%20artfully%20scattered%20on%20soft%20blush%20pink%20and%20warm%20beige%20gradient%20surface%2C%20diffused%20natural%20studio%20lighting%2C%20editorial%20lifestyle%20photography%2C%20dreamy%20minimal%20composition%20with%20generous%20negative%20space%2C%20modern%20luxury%20aesthetic%2C%20no%20visible%20text%2C%20soft%20shadows%2C%20organic%20scattered%20arrangement&width=1800&height=1000&seq=acbuy-hero-bg-v2&orientation=landscape"
          alt="ACBuySpreadsheet homepage background"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/18 to-black/35"></div>
      </div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 md:py-0">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 text-center lg:text-left">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight tracking-tight">
              ACBuySpreadsheet: The Ultimate
              <span className="block text-accent-400">ACBuy Spreadsheet Directory</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-background-50/90 max-w-xl leading-relaxed">
              Discover verified spreadsheets, product categories, buying guides, and shopping resources for ACBuy users — updated weekly.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-900 text-sm font-semibold rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer shadow-sm"
                >
                  Browse Categories
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line"></i>
                  )}
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 sm:left-0 w-64 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 max-h-64 overflow-y-auto z-40">
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => handleCategoryClick(cat.url)}
                        className="w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                ACBuy Spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-6 justify-center lg:justify-start">
              {[
                { num: '10,000+', label: 'Spreadsheet Users' },
                { num: '5,000+', label: 'Product Links' },
                { num: '14', label: 'Categories' },
                { num: 'Weekly', label: 'Updates' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-lg md:text-xl font-bold text-background-50 whitespace-nowrap">{stat.num}</div>
                  <div className="text-xs text-background-50/70 whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex flex-1 relative h-[450px]">
            <div className="absolute top-0 right-8 w-48 h-48 rounded-2xl overflow-hidden rotate-[-3deg] shadow-sm">
              <img
                src="https://readdy.ai/api/search-image?query=Pair%20of%20fashionable%20pastel%20sneakers%20with%20chunky%20sole%20on%20pale%20blush%20surface%2C%20soft%20natural%20morning%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic%2C%20warm%20neutral%20background%2C%20texture%20detail%2C%20modern%20streetwear%20style&width=400&height=400&seq=hero-collage-v2-01&orientation=squarish"
                alt="ACBuy Spreadsheet sneakers"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute top-12 left-4 w-44 h-44 rounded-2xl overflow-hidden rotate-[2deg] shadow-sm">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20knit%20sweater%20in%20warm%20camel%20tone%20folded%20neatly%20on%20light%20beige%20surface%2C%20soft%20diffused%20afternoon%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic%2C%20warm%20neutral%20background%2C%20cable%20knit%20texture%20visible&width=400&height=400&seq=hero-collage-v2-02&orientation=squarish"
                alt="ACBuy Spreadsheet sweaters"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-16 right-16 w-52 h-52 rounded-2xl overflow-hidden rotate-[1deg] shadow-sm">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20leather%20handbag%20with%20gold%20hardware%20on%20warm%20oatmeal%20surface%2C%20soft%20studio%20lighting%20with%20gentle%20highlights%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic%2C%20warm%20neutral%20background%2C%20luxury%20detail%2C%20modern%20accessory&width=450&height=450&seq=hero-collage-v2-03&orientation=squarish"
                alt="ACBuy Spreadsheet bags"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-8 left-12 w-36 h-36 rounded-2xl overflow-hidden rotate-[-2deg] shadow-sm">
              <img
                src="https://readdy.ai/api/search-image?query=Stylish%20beige%20bucket%20hat%20with%20subtle%20logo%20patch%20on%20warm%20sand%20surface%2C%20soft%20natural%20light%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic%2C%20warm%20neutral%20background%2C%20modern%20summer%20accessory&width=350&height=350&seq=hero-collage-v2-04&orientation=squarish"
                alt="ACBuy Spreadsheet headwear"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute top-32 right-0 w-32 h-32 rounded-2xl overflow-hidden rotate-[4deg] shadow-sm">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20perfume%20bottle%20with%20amber%20liquid%20and%20wooden%20cap%20on%20warm%20cream%20surface%2C%20soft%20backlighting%20creating%20glow%2C%20editorial%20product%20photography%2C%20clean%20minimal%20aesthetic%2C%20warm%20neutral%20background%2C%20glass%20reflections&width=300&height=300&seq=hero-collage-v2-05&orientation=squarish"
                alt="ACBuy Spreadsheet perfumes"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}